// Authentication Modal Component
// UPDATED: Added Sign In / Sign Up tabs for better UX
// UPDATED: Added DPDP consent checkbox for signup (Jan 31, 2026)
// Both tabs work the same way (Supabase auto-creates accounts)
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import GoogleAuthButton from './GoogleAuthButton';
import EmailOTPForm from './EmailOTPForm';
import '../../styles/auth.css';

const AuthModal = () => {
  const { showAuthModal, setShowAuthModal, clearPendingSaveContext } = useAuth();
  const [authMethod, setAuthMethod] = useState(null); // null, 'google', 'email'
  const [activeTab, setActiveTab] = useState('signin'); // 'signin' or 'signup'
  const [consentGiven, setConsentGiven] = useState(false); // DPDP consent for signup

  if (!showAuthModal) return null;

  const handleClose = () => {
    setShowAuthModal(false);
    setAuthMethod(null);
    setActiveTab('signin');
    setConsentGiven(false); // Reset consent on close
    // Clear pending save context when user closes modal without completing auth
    if (clearPendingSaveContext) {
      clearPendingSaveContext();
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setConsentGiven(false); // Reset consent when switching tabs
  };

  const handleSuccess = () => {
    setShowAuthModal(false);
    setAuthMethod(null);
    setActiveTab('signin');
    // Note: Don't clear pendingSaveContext here - it's needed for the redirect flow
  };

  const handleError = (error) => {
    console.error('Authentication error:', error);
  };

  const handleBackdropClick = (e) => {
    if (e.target.className === 'auth-modal-backdrop') {
      handleClose();
    }
  };

  const isSignUp = activeTab === 'signup';

  return (
    <div className="auth-modal-backdrop" onClick={handleBackdropClick}>
      <div className="auth-modal" data-testid="auth-modal">
        <button className="auth-modal-close" onClick={handleClose} data-testid="auth-close-button">
          ×
        </button>

        {/* Tab Navigation */}
        <div className="auth-tabs">
          <button
            className={`auth-tab ${activeTab === 'signin' ? 'active' : ''}`}
            onClick={() => handleTabChange('signin')}
          >
            Sign In
          </button>
          <button
            className={`auth-tab ${activeTab === 'signup' ? 'active' : ''}`}
            onClick={() => handleTabChange('signup')}
          >
            Sign Up
          </button>
        </div>

        <div className="auth-modal-header">
          <h2>{isSignUp ? 'Create your account' : 'Welcome back'}</h2>
          <p>
            {isSignUp
              ? 'Start planning your financial freedom today'
              : 'Sign in to access your saved plans'}
          </p>
        </div>

        <div className="auth-modal-content">
          {!authMethod ? (
            <>
              {/* DPDP Consent Checkbox - Only shown on Sign Up */}
              {isSignUp && (
                <div className="consent-section" style={{ marginBottom: '16px' }}>
                  <label
                    className="consent-checkbox"
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      lineHeight: '1.4',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={consentGiven}
                      onChange={(e) => setConsentGiven(e.target.checked)}
                      style={{
                        marginTop: '3px',
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        accentColor: 'var(--primary)',
                      }}
                    />
                    <span>
                      I agree to the{' '}
                      <Link
                        to="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--primary)' }}
                      >
                        Privacy Policy
                      </Link>{' '}
                      and consent to FINNCAL collecting and processing my personal data for
                      providing financial planning services.
                    </span>
                  </label>
                </div>
              )}

              <GoogleAuthButton
                onSuccess={handleSuccess}
                onError={handleError}
                label={isSignUp ? 'Sign up with Google' : 'Continue with Google'}
                disabled={isSignUp && !consentGiven}
              />

              <div className="auth-divider">
                <span>or</span>
              </div>

              <button
                className="email-auth-button"
                onClick={() => setAuthMethod('email')}
                disabled={isSignUp && !consentGiven}
                style={isSignUp && !consentGiven ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                {isSignUp ? 'Sign up with Email' : 'Continue with Email'}
              </button>

              {/* Consent reminder for signup */}
              {isSignUp && !consentGiven && (
                <p
                  style={{
                    fontSize: '0.8rem',
                    color: '#f59e0b',
                    textAlign: 'center',
                    marginTop: '8px',
                  }}
                >
                  Please agree to the Privacy Policy to continue
                </p>
              )}

              {/* Toggle hint */}
              <div className="auth-toggle-hint">
                {isSignUp ? (
                  <p>
                    Already have an account?{' '}
                    <button onClick={() => handleTabChange('signin')}>Sign in</button>
                  </p>
                ) : (
                  <p>
                    Don't have an account?{' '}
                    <button onClick={() => handleTabChange('signup')}>Sign up</button>
                  </p>
                )}
              </div>

              <div className="auth-footer">
                <p>
                  By continuing, you agree to our{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </p>
              </div>
            </>
          ) : authMethod === 'email' ? (
            <>
              <button className="auth-back-button" onClick={() => setAuthMethod(null)}>
                ← Back to options
              </button>
              <EmailOTPForm onSuccess={handleSuccess} onError={handleError} isSignUp={isSignUp} />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
